import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

/**
 * Export data to a real Excel (.xlsx) format using the 'xlsx' library.
 * This generates a native Excel file structure that prevents format/extension mismatch warnings.
 * 
 * @param widgetId - Unique identifier for the widget, used as the worksheet name and part of the filename.
 * @param headers - Array of strings representing the table headers.
 * @param rows - 2D array of values representing the table rows.
 */
export function downloadAsExcel(widgetId: string, headers: string[], rows: any[][]): void {
  // Title Case: capitalize first letter of each word
  const titleCase = widgetId
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
  const fileName = `${titleCase.replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_')}.xlsx`;

  // Combine headers and rows into a single data array for XLSX
  const data = [headers, ...rows];

  // Create a new workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Set column widths (optional, but makes it look better)
  const colWidths = headers.map(h => ({ wch: Math.max(h.length, 12) }));
  worksheet['!cols'] = colWidths;

  // Add the worksheet to the workbook
  // Note: worksheet name must be <= 31 chars and avoid special chars
  const sheetName = titleCase.replace(/[:\\?*\[\]\/]/g, '').substring(0, 31) || 'Sheet1';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate binary Excel data
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  // Create a Blob and save it
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, fileName);
}
